import { formatCurrency, ExternalLink, ExternalLinkButton } from "uikit"
import api from "api"
import AccountsConnector from "components/accounts/AccountsConnector"
import HumanTimeSince from "components/HumanTimeSince"
import React, { useState, useCallback, memo } from "react"
import { useStore } from "store"
import { Link } from "react-router-dom"
import {
  Button,
  Dropdown,
  Grid,
  Card,
  Header,
  Icon,
  Image,
  Loader,
  Modal
} from "semantic-ui-react"
import {
  propertyCategoryLabels,
  propertyTypes
} from "../../properties/propertyTypes"
import { accountCategoryLabels, accountTypes } from "../accountTypes"
import { SetupGuide } from "../SetupGuide"
import "./AccountsAssetsTable.css"
import personalloans1 from "./evenfi_personalloans_1.png"
import personalloans2 from "./evenfi_personalloans_2.png"
import * as Sentry from "@sentry/react"

const CategoryHeaderRow = memo(({ category }) => (
  <Grid.Row
    style={{ paddingTop: "0" }}
    className="AccountsGrid__CategoryHeader"
  >
    <Grid.Column style={{ background: "#f3f3f3" }}>
      <h4 style={{ padding: "0.5em" }}>{category}</h4>
    </Grid.Column>
  </Grid.Row>
))

const AccountAssetsTableRow = ({
  id,
  name,
  description,
  value,
  balance_updated_at,
  image_url,
  type,
  is_confirmed,
  rowType,
  connection_refresh_status,
  connection_id,
  updateCredentials,
  isBeingRefreshed
}) => {
  const daysSinceUpdate =
    new Date() - new Date(balance_updated_at) / (1000 * 60 * 60 * 24)
  return (
    <Grid.Row columns="equal" className="AccountsGrid__AccountRow">
      <Grid.Column textAlign="center" width={2} verticalAlign="middle">
        {image_url ? (
          <Image src={image_url} centered className="AccountsGrid__BankImage" />
        ) : (
          <Icon
            name={
              (accountTypes[type] && accountTypes[type].icon) ||
              (propertyTypes[type] && propertyTypes[type].icon) ||
              "university"
            }
            size="big"
          />
        )}
      </Grid.Column>
      <Grid.Column width={10} verticalAlign="middle">
        <Header>
          {is_confirmed && !isBeingRefreshed ? (
            <Link to={`/organize/accounts+property/${rowType}/${id}/${type}`}>
              <Header.Content
                className={`AccountsGrid__AccountName ${
                  is_confirmed && !isBeingRefreshed ? "" : "greyed-text"
                }`}
              >
                {name.split("_").join(" ")}
                <Icon name="chevron right" size="small" />
              </Header.Content>
            </Link>
          ) : (
            <Header.Content
              className={`AccountsGrid__AccountName ${
                is_confirmed && !isBeingRefreshed ? "" : "greyed-text"
              }`}
            >
              {name.split("_").join(" ")}
              <Icon name="chevron right" size="small" />
            </Header.Content>
          )}
          {description ? (
            <Header.Subheader className="AccountsGrid__AccountDescription t">
              <p style={{ margin: "0" }}>{description.split("_").join(" ")}</p>
            </Header.Subheader>
          ) : null}
        </Header>
      </Grid.Column>
      <Grid.Column textAlign="right">
        {isBeingRefreshed ? (
          <div>
            <p className="AccountsGrid__Balance greyed-text">
              <Icon loading name="refresh" />
            </p>

            <p style={{ margin: "0" }} className="greyed-text">
              {!isBeingRefreshed &&
              connection_refresh_status != null &&
              connection_id &&
              connection_refresh_status !== "in_progress" &&
              connection_refresh_status !== "ok" ? (
                <span style={{ marginRight: "1em" }}>
                  <Button
                    color="red"
                    basic
                    size="mini"
                    content="Update Credentials"
                    onClick={() => updateCredentials(connection_id)}
                  />
                </span>
              ) : null}
              {isBeingRefreshed ? null : (
                <span>
                  <HumanTimeSince time={balance_updated_at} /> ago
                </span>
              )}
            </p>
          </div>
        ) : is_confirmed ? (
          <div>
            <p className="AccountsGrid__Balance">
              {formatCurrency(value || 0)}
            </p>

            <p style={{ margin: "0" }} className="greyed-text">
              {(connection_refresh_status != null &&
                connection_id &&
                connection_refresh_status !== "in_progress" &&
                connection_refresh_status !== "ok") ||
              (daysSinceUpdate > 3 && connection_id) ? (
                <span style={{ marginRight: "1em" }}>
                  <Button
                    color="red"
                    basic
                    size="mini"
                    content="Update Credentials"
                    onClick={() => updateCredentials(connection_id)}
                  />
                </span>
              ) : null}
              <span>
                <HumanTimeSince time={balance_updated_at} /> ago
              </span>
            </p>
          </div>
        ) : (
          <Link
            to={`/organize/accounts+property/accounts/confirm/${id}/${type}`}
          >
            <Button color="yellow" content="Confirm Details" />
          </Link>
        )}
      </Grid.Column>
    </Grid.Row>
  )
}

const AddAccountsAssetsRow = ({
  baseUrl,
  showAccountsConnector,
  refreshConnections,
  isRefreshingAccounts
}) => (
  <Grid.Row
    style={{ minHeight: "5em" }}
    columns={1}
    className="Accounts__AddAccountRow"
  >
    <Grid.Column textAlign="center">
      {isRefreshingAccounts ? (
        <Button icon labelPosition="right" primary disabled>
          Refreshing...
          <Icon loading name="refresh" />
        </Button>
      ) : (
        <React.Fragment>
          <Button.Group primary>
            <Button onClick={showAccountsConnector}>Link Account</Button>
            <Dropdown floating button className="icon">
              <Dropdown.Menu>
                <Dropdown.Item onClick={refreshConnections}>
                  Refresh Linked Accounts
                </Dropdown.Item>
                <Dropdown.Item as={Link} to="/manage-linked-accounts">
                  Manage Linked Accounts
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Button.Group>
          <Button primary style={{ marginLeft: "20px" }}>
            <Link
              to={`${baseUrl}/accounts/new`}
              data-testid="addAccountManually"
              className="navlink"
              style={{ color: "white" }}
            >
              Add Manually
            </Link>
          </Button>
          <div style={{ paddingTop: "0.5em" }}>
            <p className="greyed-text">
              You can also{" "}
              <Link to={`${baseUrl}/properties/new`}>add property</Link>
            </p>
          </div>
        </React.Fragment>
      )}
    </Grid.Column>
  </Grid.Row>
)

export const AccountsAssetsTable = props => {
  const {
    accounts,
    people,
    properties,
    baseUrl,
    household,
    showAccountsConnector,
    refreshConnections,
    updateHouseholdConnectionsRefreshStatus,
    householdState,
    showOffers,
    connections
  } = props

  const householdId = household.id
  const [showAccounts, setShowAccounts] = useState(false)
  const [providerId, setProviderId] = useState("")
  const [isPending, setIsPending] = useState(false)

  const updateCredentials = useCallback(
    connection_id => {
      setIsPending(true)
      try {
        const { id: provider_account_id } = connections.find(
          ({ id }) => id === connection_id
        )
        setProviderId(provider_account_id)
        setShowAccounts(true)
        setIsPending(false)
      } catch (error) {
        setShowAccounts(true)
        setIsPending(false)
      }
    },
    [connections]
  )

  const normalizedAccounts = useCallback(
    () =>
      accounts.map(account => ({
        ...account,
        value: account.balance || account.death_benefit,
        isBeingRefreshed: (
          householdState["refreshingConnectingIds"] || []
        ).find(id => account.connection_id === id),
        rowType: "accounts"
      })),
    [accounts, householdState]
  )
  const types = accounts.map(acc => acc.type)
  const hasTermLife = types.some(type => type === "termlife")

  const completedSteps = useCallback(
    () =>
      types.reduce((prev, curr) => {
        if (accountTypes[curr]) {
          const categoryText = accountTypes[curr].category
          if (prev.indexOf(categoryText) < 0) {
            prev.push(categoryText)
          }
        } else {
          try {
            Sentry.captureMessage(`Unknown account type: ${curr}`, "warning")
          } catch (error) {
            console.error("Unknown account type:", curr)
          }
        }
        return prev
      }, []),
    [types]
  )

  const normalizedProperties = useCallback(
    () =>
      properties.map(property => ({
        ...property,
        balance_updated_at: property.updated_at,
        name: property.description,
        rowType: "properties",
        description: property.is_primary_residence ? "Primary Residence" : ""
      })),
    [properties]
  )

  const accountAssetsRows = useCallback(
    () =>
      categorizeRows(
        accountCategoryLabels,
        accountTypes,
        normalizedAccounts(),
        updateCredentials,
        householdId,
        showOffers
      ).concat(
        categorizeRows(
          propertyCategoryLabels,
          propertyTypes,
          normalizedProperties(),
          updateCredentials,
          householdId,
          showOffers
        )
      ),
    [
      householdId,
      showOffers,
      updateCredentials,
      normalizedAccounts,
      normalizedProperties
    ]
  )

  const isntSingle = Object.values(people).some(
    person => person.role !== "client"
  )

  return (
    <Grid className="AccountsGrid">
      <Loader active={isPending} />
      {accountAssetsRows()}
      <AddAccountsAssetsRow
        isRefreshingAccounts={householdState["isRefreshingAccounts"]}
        baseUrl={baseUrl}
        household={household}
        showAccountsConnector={showAccountsConnector}
        refreshConnections={refreshConnections}
      />
      <Grid.Row>
        <Grid.Column textAlign="center">
          <SetupGuide completedSteps={completedSteps()} />
          {showOffers && !hasTermLife && isntSingle && (
            <p>
              <ExternalLink href="https://www.quotacy.com/savvi-financial-term-life-insurance/">
                No life insurance? Get free quotes and compare policies at
                Quotacy.
              </ExternalLink>
            </p>
          )}
        </Grid.Column>
      </Grid.Row>
      {showAccounts ? (
        <AccountsConnector
          getFastLink={messageId =>
            `${api.baseURL}households/${householdId}/connections/linker?${messageId}&${providerId}&refresh`
          }
          household={household}
          onSuccess={() => {
            updateHouseholdConnectionsRefreshStatus(householdId).then(() => {
              setShowAccounts(false)
            })
          }}
        />
      ) : null}
    </Grid>
  )
}

const EvenSavingsOffersTeaser = props => {
  const [showAllOffers, changeShowAllOffers] = useState(false)
  const { householdId } = props
  const [householdOffers] = useStore("householdOffers", householdId)

  const toggleShowAllOffers = () => {
    changeShowAllOffers(prev => !prev)
  }

  const savingsOffers = householdOffers.savings.data.savingsOffers
  return savingsOffers && savingsOffers.length > 0 ? (
    <>
      <EvenSavingsOffer
        savingsOffer={savingsOffers[0]}
        toggleShowAllOffers={toggleShowAllOffers}
        hideShowAll={savingsOffers.length <= 1}
      />
      <Modal size="large" open={showAllOffers} onClose={toggleShowAllOffers}>
        <Modal.Header>Savings Account Offers</Modal.Header>
        <Modal.Content>
          <EvenSavingsOffers savingsOffers={savingsOffers} />
        </Modal.Content>
        <Modal.Actions>
          <Button primary onClick={toggleShowAllOffers}>
            Close
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  ) : null
}

function EvenSavingsOffers({ savingsOffers }) {
  return (
    <Grid>
      {savingsOffers.map(savingsOffer => (
        <EvenSavingsOffer
          savingsOffer={savingsOffer}
          hideShowAll={true}
          key={savingsOffer.uuid}
        />
      ))}
    </Grid>
  )
}

function EvenSavingsOffer({ savingsOffer, hideShowAll, toggleShowAllOffers }) {
  return (
    <Grid.Row key={savingsOffer.uuid}>
      <Card
        fluid
        style={{ marginLeft: "3em", marginRight: "3em", marginBottom: "1.5em" }}
      >
        <Card.Content>
          <Grid stackable>
            <Grid.Row
              columns={2}
              style={{
                paddingTop: "8px",
                paddingBottom: "0px",
                fontSize: "small",
                color: "#ddd"
              }}
            >
              <Grid.Column>SAVINGS ACCOUNT OFFER</Grid.Column>
              <Grid.Column textAlign="right">
                as of {savingsOffer.details.effectiveAsOf.substring(0, 10)}
              </Grid.Column>
            </Grid.Row>
            <Grid.Row style={{ paddingTop: "0px" }}>
              <Grid.Column width={3} verticalAlign="middle">
                <Image size="small" src={savingsOffer.partner.imageUrl} />
              </Grid.Column>
              <Grid.Column width={2} verticalAlign="middle">
                <h1 style={{ marginBottom: "0px" }}>
                  {savingsOffer.details.annualPercentYield}%
                </h1>
                <span style={{ color: "#888" }}>APY</span>
              </Grid.Column>
              <Grid.Column
                width={3}
                verticalAlign="middle"
                style={{ fontSize: "12px" }}
              >
                Monthly fee: ${savingsOffer.details.monthlyFee}
                <br />
                Check writing:{" "}
                {savingsOffer.details.checkWriting ? "Yes" : "No"}
              </Grid.Column>
              <Grid.Column
                width={4}
                verticalAlign="middle"
                style={{ fontSize: "12px" }}
              >
                Min. balance to avoid fees: $
                {savingsOffer.details.minimumDepositWithFees}
                <br />
                Min. balance to earn APY: ${savingsOffer.details.minimumDeposit}
              </Grid.Column>
              <Grid.Column width={4} verticalAlign="middle" textAlign="right">
                <ExternalLinkButton primary compact href={savingsOffer.url}>
                  Open Account
                </ExternalLinkButton>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row
              columns={2}
              style={{
                paddingTop: "0px",
                paddingBottom: "8px"
              }}
            >
              <Grid.Column
                style={{
                  fontSize: "small",
                  color: "#ddd"
                }}
              >
                <Modal
                  trigger={
                    <Button
                      basic
                      size="mini"
                      style={{ border: "none", padding: 0, boxShadow: "none" }}
                    >
                      DISCLAIMER AND MORE INFO
                    </Button>
                  }
                  closeOnEscape={true}
                  closeOnDimmerClick={true}
                  closeIcon={true}
                >
                  <Modal.Content>
                    <Modal.Description>
                      <Header>{savingsOffer.details.name}</Header>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: savingsOffer.details.description
                        }}
                      />
                      <h4>Disclaimer</h4>
                      <p>{savingsOffer.details.details}</p>
                      <h4>Disclosure</h4>
                      <p>
                        Savings account offers are facilitated by Even
                        Financial, Inc. Even Financial enables us to connect our
                        users with real-time decisions and personalized offers
                        from premium financial services providers. Even
                        Financial and SAVVI Financial may receive compensation
                        for this offer. This compensation may influence the
                        selection, appearance, and ordering of the offers you
                        see. However, this compensation also makes it possible
                        for SAVVI Financial to provide certain services to you
                        at no charge. The offers shown to you do not include all
                        financial services companies or all of their available
                        product and service offerings. We cannot and do not
                        guarantee that these are the best rates available to
                        you.
                      </p>
                    </Modal.Description>
                  </Modal.Content>
                </Modal>
              </Grid.Column>
              <Grid.Column textAlign="right">
                {!hideShowAll && (
                  <Button
                    className="EvenSavingsOffer__all"
                    onClick={toggleShowAllOffers}
                  >
                    See all offers>
                  </Button>
                )}
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Card.Content>
      </Card>
    </Grid.Row>
  )
}

const EvenPersonalLoanOffer = memo(() => {
  return (
    <Grid.Row key="__personalloan_offer__">
      <Card
        fluid
        style={{ marginLeft: "3em", marginRight: "3em", marginBottom: "1.5em" }}
      >
        <Card.Content>
          <Grid stackable>
            <Grid.Row columns={1}>
              <Grid.Column>
                <h4>
                  Consolidate Your Debt with Personalized Loan Offers from
                  Lenders like:
                </h4>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row style={{ paddingTop: "0px" }}>
              <Grid.Column width={6} verticalAlign="middle">
                <img
                  src={personalloans1}
                  alt="LendingClub, Best Egg, Payoff, Upgrade"
                />
              </Grid.Column>
              <Grid.Column width={6} verticalAlign="middle">
                <img
                  src={personalloans2}
                  alt="Prosper, SoFi, Lending Point, FreedomPlus"
                />
              </Grid.Column>
              <Grid.Column width={4} verticalAlign="middle" textAlign="right">
                <ExternalLinkButton
                  primary
                  compact
                  href="https://hifiona.com/savvi/loans"
                >
                  Get Offers
                </ExternalLinkButton>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row
              style={{
                paddingTop: "0px",
                paddingBottom: "8px"
              }}
            >
              <Grid.Column
                width={6}
                style={{
                  fontSize: "small",
                  color: "#ddd"
                }}
              >
                <Modal
                  trigger={
                    <Button
                      basic
                      size="mini"
                      style={{ border: "none", padding: 0, boxShadow: "none" }}
                    >
                      DISCLAIMER AND MORE INFO
                    </Button>
                  }
                  closeOnEscape={true}
                  closeOnDimmerClick={true}
                  closeIcon={true}
                >
                  <Modal.Content>
                    <Modal.Description>
                      <Header>
                        Get Matched with Personalized Loan Offers from Top
                        Providers
                      </Header>
                      <p>
                        This service is free and will <b>NOT</b> affect your
                        credit score. It's Quick too!
                      </p>
                      <h4>1. Answer a few questions</h4>
                      Just fill out our simple form and to check offers from all
                      the top providers in seconds.
                      <h4>1. Get matched with top providers</h4>
                      Based on the info you provide and your creditworthiness,
                      get matched with the right offer to meet your needs.
                      <h4>3. Close the deal</h4>
                      Choose the offer with the best terms for you and, if
                      approved, get your funds or new account right away.
                      <h4>Disclosure</h4>
                      <p>
                        Personal loan offers are facilitated by Even Financial,
                        Inc. Even Financial enables us to connect our users with
                        real-time decisions and personalized offers from premium
                        financial services providers. Even Financial and SAVVI
                        Financial may receive compensation for this offer. This
                        compensation may influence the selection, appearance,
                        and ordering of the offers you see. However, this
                        compensation also makes it possible for SAVVI Financial
                        to provide certain services to you at no charge. The
                        offers shown to you do not include all financial
                        services companies or all of their available product and
                        service offerings. We cannot and do not guarantee that
                        these are the best rates available to you.
                      </p>
                    </Modal.Description>
                  </Modal.Content>
                </Modal>
              </Grid.Column>
              <Grid.Column
                width={10}
                textAlign="right"
                style={{ fontSize: "13px" }}
              >
                Shop and compare personalized loan offers in less than 60
                seconds.
              </Grid.Column>
            </Grid.Row>
          </Grid>
          ]
        </Card.Content>
      </Card>
    </Grid.Row>
  )
})

const categorizeRows = (
  labelMap,
  typeMap,
  items,
  updateCredentials,
  householdId,
  showOffers
) => {
  let rows = []
  labelMap.forEach((categoryLabel, category) => {
    let categorizedRows = []
    let categorizedItems = items.filter(
      item => typeMap[item.type] && typeMap[item.type].category === category
    )
    if (categorizedItems.length > 0) {
      const itemsUnderCategory = categorizedItems.map(item => (
        <AccountAssetsTableRow
          {...item}
          key={item.id}
          updateCredentials={updateCredentials}
        />
      ))
      categorizedItems.length > 0 &&
        categorizedRows.push(
          <CategoryHeaderRow
            category={categoryLabel.toUpperCase()}
            key={category}
          />
        )

      rows = rows.concat(categorizedRows.concat(itemsUnderCategory))

      if (showOffers) {
        const hasMortgage = items.some(item => item.type === "frm")
        const hasStudentLoan = items.some(item => item.type === "studentloan")

        if (hasMortgage && category === "loan") {
          rows.push(
            <div
              key="__mortgage_offer__"
              style={{ width: "100%", textAlign: "center", padding: "16px" }}
            >
              {/* <ExternalLink
                href="https://track.flexlinkspro.com/a.ashx?foid=1163958.138263197&foc=2&fot=9999&fos=1"
              >
                <img
                  border="0"
                  src="https://content.flexlinks.com/sharedimages/products/138263197/2645521.png"
                  tyle={{ maxWidth: "100%" }}
                />
              </ExternalLink>
              <img
                src="https://track.flexlinkspro.com/i.ashx?foid=1163958.138263197&fot=9999&foc=2&fos=1"
                border="0"
                width="0"
                height="0"
                style={{ opacity: "0" }}
              /> */}
              <ExternalLink href="https://track.flexlinkspro.com/a.ashx?foid=1163958.140096328&foc=2&fot=9999&fos=1">
                <img
                  border="0"
                  src="https://content.flexlinks.com/sharedimages/products/140096328/3813166.gif"
                  style={{ maxWidth: "100%" }}
                  alt="Advertisement"
                />
              </ExternalLink>
              <img
                src="https://track.flexlinkspro.com/i.ashx?foid=1163958.140096328&fot=9999&foc=2&fos=1"
                border="0"
                width="0"
                height="0"
                style={{ opacity: "0" }}
                alt=""
              />
            </div>
          )
        }
        if (hasStudentLoan && category === "loan") {
          rows.push(
            <div
              key="__studentloan__"
              style={{ width: "100%", textAlign: "center", padding: "16px" }}
            >
              <ExternalLink href="https://track.flexlinkspro.com/a.ashx?foid=1163958.141458176&foc=2&fot=9999&fos=1">
                <img
                  border="0"
                  src="https://content.flexlinks.com/sharedimages/products/141458176/4541820.png"
                  style={{ maxWidth: "100%" }}
                  alt="Advertisement"
                />
              </ExternalLink>
              <img
                src="https://track.flexlinkspro.com/i.ashx?foid=1163958.141458176&fot=9999&foc=2&fos=1"
                border="0"
                width="0"
                height="0"
                style={{ opacity: "0" }}
                alt=""
              />
            </div>
          )
        }
        if (category === "realestate") {
          rows.push(
            <div
              key="__homeequityloan__"
              style={{ width: "100%", textAlign: "center", padding: "16px" }}
            >
              <ExternalLink href="https://track.flexlinkspro.com/a.ashx?foid=1163958.140096326&foc=2&fot=9999&fos=1">
                <img
                  border="0"
                  src="https://content.flexlinks.com/sharedimages/products/140096326/3813147.gif"
                  style={{ maxWidth: "100%" }}
                  alt="Advertisement"
                />
              </ExternalLink>
              <img
                src="https://track.flexlinkspro.com/i.ashx?foid=1163958.140096326&fot=9999&foc=2&fos=1"
                border="0"
                width="0"
                height="0"
                style={{ opacity: "0" }}
                alt=""
              />
            </div>
          )
          rows.push(
            <div
              key="__homeinsurance__"
              style={{ width: "100%", textAlign: "center", padding: "16px" }}
            >
              <ExternalLink href="https://policygenius.go2cloud.org/aff_c?offer_id=120&aff_id=1650&redirect_slug=savvi-homeowners">
                <img
                  src="https://d37p6u34ymiu6v.cloudfront.net/1-md5-71fa073622adc09eee388f3cc24be753.jpg"
                  alt="Advertisement"
                  style={{ maxWidth: "100%" }}
                  width="728"
                  height="90"
                />
              </ExternalLink>
            </div>
          )
        }
        if (category === "car") {
          rows.push(
            <div
              key="__carinsurance__"
              style={{ width: "100%", textAlign: "center", padding: "16px" }}
            >
              <ExternalLink href="https://track.flexlinkspro.com/a.ashx?foid=1163958.138135242&foc=2&fot=9999&fos=1">
                <img
                  border="0"
                  src="https://content.flexlinks.com/sharedimages/products/138135242/2588968.jpg"
                  style={{ maxWidth: "100%" }}
                  alt="Advertisement"
                />
              </ExternalLink>
              <img
                src="https://track.flexlinkspro.com/i.ashx?foid=1163958.138135242&fot=9999&foc=2&fos=1"
                border="0"
                width="0"
                height="0"
                style={{ opacity: "0" }}
                alt=""
              />
            </div>
          )
        }
        if (category === "bank") {
          rows.push(
            <EvenSavingsOffersTeaser
              key="__savings_offer__"
              householdId={householdId}
            />
          )
        }
        // if (category === "bank") {
        //   rows.push(
        //     <div
        //       key="__savings__"
        //       style={{ width: "100%", textAlign: "center", padding: "16px" }}
        //     >
        //       <ExternalLink href="https://www.mb103.com/lnk.asp?o=15671&c=918277&a=390431&k=991D52EF53EA23992AAAEFADE713353A&l=17196 ">
        //         <img
        //           border="0"
        //           src="https://www.mb103.com/getimage.asp?a=390431&m=7239&o=15671&i=139601.dat"
        //           style={{ width: "100%", maxWidth: "640" }}
        //           alt="Advertisement"
        //         />
        //       </ExternalLink>
        //     </div>
        //   )
        // }
        if (category === "creditcard") {
          if (categorizedItems.some(account => !account.is_noncarried)) {
            rows.push(<EvenPersonalLoanOffer key="__personalloan_offer__" />)
          }
        }
      }
    }
  })
  return rows
}
